import React, { useEffect, useRef, useState } from 'react'
import { useLocation } from 'react-router-dom'
import { useSelector } from 'react-redux'
import styled, { keyframes, css } from 'styled-components'
import { getCurrentUser } from 'features/auth/ducks/selectors'
import { useFloatingHidden } from 'components/atoms/floatingStack'

const SETTLE_MS = 600
const MAX_MS = 30000
const POLL_MS = 100

const net = { inFlight: 0, lastEnd: 0, patched: false }

const patchNetwork = () => {
  if (net.patched || typeof window === 'undefined') return
  net.patched = true

  const done = () => {
    net.inFlight = Math.max(0, net.inFlight - 1)
    net.lastEnd = performance.now()
  }

  const originalFetch = window.fetch
  if (originalFetch) {
    window.fetch = function (...args) {
      net.inFlight++
      return originalFetch.apply(this, args).then(
        response => { done(); return response },
        error => { done(); throw error }
      )
    }
  }

  const originalSend = XMLHttpRequest.prototype.send
  XMLHttpRequest.prototype.send = function (...args) {
    net.inFlight++
    this.addEventListener('loadend', done, { once: true })
    return originalSend.apply(this, args)
  }
}

const emailOf = user => user?.email || user?.user?.email || null

const isArtificially = user => {
  const email = emailOf(user)
  return typeof email === 'string' && email.trim().toLowerCase().endsWith('@artificially.io')
}

const fmt = ms => (ms == null ? '–' : ms < 1000 ? `${Math.round(ms)} ms` : `${(ms / 1000).toFixed(2)} s`)

const colourFor = ms => (ms == null ? '#94a3b8' : ms < 800 ? '#22c55e' : ms < 2000 ? '#f59e0b' : '#ef4444')

const normalisePath = path =>
  (path || '/')
    .split('/')
    .map(seg => {
      if (!seg) return seg
      if (/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}/i.test(seg)) return ':id'
      if (/^\d+$/.test(seg)) return ':id'
      return seg
    })
    .join('/') || '/'

const LoadTimeWidget = () => {
  const user = useSelector(getCurrentUser)
  const staff = isArtificially(user)
  const floatingHidden = useFloatingHidden()
  const location = useLocation()
  const [ms, setMs] = useState(null)
  const [busy, setBusy] = useState(false)
  const [open, setOpen] = useState(false)
  const [, force] = useState(0)
  const history = useRef(new Map())
  const firstRef = useRef(true)

  const record = (path, total) => {
    const map = history.current
    const prev = map.get(path) || { last: 0, max: 0, count: 0 }
    map.set(path, { last: total, max: Math.max(prev.max, total), count: prev.count + 1 })
  }

  useEffect(() => {
    if (staff) patchNetwork()
  }, [staff])

  useEffect(() => {
    if (!staff) return
    try {
      const nav = performance.getEntriesByType('navigation')[0]
      if (nav && nav.duration) {
        const total = Math.round(nav.duration)
        setMs(total)
        record(normalisePath(window.location.pathname), total)
      }
    } catch (e) { /* noop */ }
  }, [staff])

  useEffect(() => {
    if (!staff) return undefined
    if (firstRef.current) {
      firstRef.current = false
      return undefined
    }

    const navStart = performance.now()
    let sawRequest = false
    let stopped = false
    let idleAt = null
    let timer = null
    setBusy(true)
    setMs(0)

    const tick = () => {
      if (stopped) return
      const now = performance.now()

      if (net.inFlight > 0) {
        // Network busy → count up live so you can watch it tick.
        sawRequest = true
        idleAt = null
        setMs(Math.round(now - navStart))
      } else {
        // Network quiet → freeze on the honest moment the last request
        // finished (net.lastEnd), not the idle-grace time. No snap-back:
        // if a late request fires, we resume counting above.
        if (idleAt === null) idleAt = now
        setMs(sawRequest ? Math.max(0, Math.round(net.lastEnd - navStart)) : 0)
        if (now - idleAt >= SETTLE_MS) {
          stopped = true
          setBusy(false)
          record(normalisePath(location.pathname), sawRequest ? Math.max(0, Math.round(net.lastEnd - navStart)) : 0)
          return
        }
      }

      if (now - navStart >= MAX_MS) {
        stopped = true
        setBusy(false)
        record(normalisePath(location.pathname), Math.round(now - navStart))
        return
      }
      timer = setTimeout(tick, POLL_MS)
    }
    timer = setTimeout(tick, POLL_MS)

    return () => {
      stopped = true
      clearTimeout(timer)
    }
  }, [location.pathname, staff])

  if (!staff || floatingHidden) return null

  const rows = [...history.current.entries()]
    .map(([path, stats]) => ({ path, ...stats }))
    .sort((a, b) => b.max - a.max)

  return (
    <Wrap>
      {open && (
        <Panel>
          <PanelHead>
            <span>Page load times · slowest first</span>
            <Clear onClick={() => { history.current.clear(); force(n => n + 1) }}>clear</Clear>
          </PanelHead>
          {rows.length === 0
            ? <Empty>Navigate around to record page loads…</Empty>
            : rows.map(row => (
              <Row key={row.path}>
                <Path title={row.path}>{row.path}</Path>
                <Times>
                  <Time $colour={colourFor(row.max)}>{fmt(row.max)}</Time>
                  {row.count > 1 && <Count>last {fmt(row.last)} · ×{row.count}</Count>}
                </Times>
              </Row>
            ))}
        </Panel>
      )}
      <Badge onClick={() => setOpen(o => !o)} title='Live page-load stopwatch: counts up while the network is busy, freezes when the last request finishes. Click for per-page list. Only shown to @artificially.io.'>
        <Bolt aria-hidden='true'>⚡</Bolt>
        <Val $colour={colourFor(ms)} $busy={busy}>{fmt(ms)}</Val>
        <Caret aria-hidden='true'>{open ? '▾' : '▸'}</Caret>
      </Badge>
    </Wrap>
  )
}

export default LoadTimeWidget

const pulse = keyframes`0%,100%{opacity:1}50%{opacity:0.45}`

const Wrap = styled.div`
  position: fixed;
  bottom: 14px;
  left: 14px;
  z-index: 2147483000;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 8px;

  @media (max-width: 768px) {
    left: 12px;
    bottom: var(--bp-float-1);
    z-index: 900;
  }
`

const Badge = styled.button`
  font: inherit;
  border: none;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 5px 11px;
  border-radius: 999px;
  background: rgba(15, 23, 42, 0.92);
  color: #e2e8f0;
  font-size: 12px;
  font-weight: 700;
  font-variant-numeric: tabular-nums;
  line-height: 1;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.28);
  cursor: pointer;
  user-select: none;
`

const Bolt = styled.span`
  color: #fbbf24;
  font-size: 12px;
`

const Val = styled.span`
  color: ${({ $colour }) => $colour};
  ${({ $busy }) => $busy && css`animation: ${pulse} 1s ease-in-out infinite;`}
`

const Caret = styled.span`
  color: #64748b;
  font-size: 10px;
`


const Panel = styled.div`
  width: 340px;
  max-height: 50vh;
  overflow-y: auto;
  background: rgba(15, 23, 42, 0.96);
  color: #e2e8f0;
  border-radius: 10px;
  box-shadow: 0 6px 24px rgba(0, 0, 0, 0.4);
  padding: 8px;
  font-size: 12px;
  font-variant-numeric: tabular-nums;
`

const PanelHead = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 4px 6px 8px;
  color: #94a3b8;
  font-weight: 700;
  border-bottom: 1px solid rgba(148, 163, 184, 0.2);
`

const Clear = styled.button`
  font: inherit;
  border: none;
  background: none;
  color: #60a5fa;
  cursor: pointer;
  font-weight: 700;
`

const Empty = styled.div`
  padding: 12px 6px;
  color: #64748b;
`

const Row = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  padding: 6px;
  border-radius: 6px;

  &:hover { background: rgba(148, 163, 184, 0.1); }
`

const Path = styled.span`
  flex: 1 1 auto;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  color: #cbd5e1;
`

const Times = styled.span`
  flex: 0 0 auto;
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 2px;
`

const Time = styled.span`
  color: ${({ $colour }) => $colour};
  font-weight: 700;
`

const Count = styled.span`
  color: #64748b;
  font-size: 10px;
`
